import CloseIcon from '@mui/icons-material/Close'
import { IconButton, Link } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LogoMobileES from '../../../assets/images/logos/LogoCircle1_ES.svg'
import LogoMobilePT from '../../../assets/images/logos/LogoCircle1_PT.svg'
import MBrasil from '../../../assets/images/mapa_brasil.png'
import MColombia from '../../../assets/images/mapa_colombia.png'
import MPeru from '../../../assets/images/mapa_peru.png'
import FiltersContext from '../../../Context/FiltersContext'
import i18n from '../../../i18n/i18n'
import { COUNTRY_COLORS, CountryKeys } from '../../constants/country-color'
import './ProjectModal.scss'

export const ProjectModal = ({ containerRef, posTop }: { containerRef: React.RefObject<HTMLDivElement>, posTop: number }) => {
  const { t }: any = useTranslation()
  const { projectSelected, setProjectSelected } = useContext(FiltersContext)
  const [country, setCountry] = useState<CountryKeys>('peru')
  const [projectLinks, setProjectLinks] = useState<string[]>([])

  const usFormatter = new Intl.NumberFormat('en-US');
  const esFormatter = new Intl.NumberFormat('es-CO');
  const ptFormatter = new Intl.NumberFormat('pt-BR');

  const isTallIframe = window.innerHeight > 2100

  // Function to determine which logo to display based on the language and device type
  const findLogo = () => {
    return i18n.language === 'es' || i18n.language.includes('en')
      ? LogoMobileES
      : LogoMobilePT
  }

  const formatArea = (area: number) => {
    if (i18n.language === 'es') {
      return esFormatter.format(area)
    } else if (i18n.language === 'pt') {
      return ptFormatter.format(area)
    } else {
      return usFormatter.format(area)
    }
  }

  useEffect(() => {
    if (!!projectSelected) {
      setCountry(
        projectSelected.id_pais
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
      )

      const links = [
        projectSelected['link1'],
        projectSelected['link2'],
        projectSelected['link3'],
      ].filter((link) => !!link)

      setProjectLinks(links)
    }
  }, [projectSelected])

  const handleProjectModalClose = () => {
    setProjectSelected(null)
  }

  return (
    <>
      {projectSelected ? (
        <Dialog
          open={!!projectSelected}
          onClose={handleProjectModalClose}
          fullWidth={true}
          maxWidth="xl"
          className="project-modal"
          container={containerRef.current} // hace que el modal se renderice dentro del contenedor padre
          {...(isTallIframe && {
            // si el iframe es muy alto, hace que el modal se posicione de forma absoluta
            scroll: 'body',
            slotProps: {
              paper: {
                sx: {
                  position: 'absolute',
                  top: posTop,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  margin: 0,
                },
              },
            },
          })}
        >
          <DialogTitle
            id="responsive-dialog-title"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              paddingTop: 0,
              position: 'relative',
            }}
          >
            <span className="project-modal_logo">
              {/* <img src={findLogo()} /> */}
              <img
                // style={{ width: 165, height: 146 }}
                src={findLogo()}
                alt="Carbono Opaco"
              />
            </span>
            <div
              className="project-title_container"
              style={{
                borderLeft: `24px solid ${COUNTRY_COLORS[country]}`,
              }}
            >
              <span className="project-title">
                {projectSelected.nombre_proyecto}
              </span>
              {projectSelected.website_certificador ? (
                <Link
                  href={projectSelected.website_certificador}
                  color="#95C11C"
                  sx={{
                    typography: 'body1',
                  }}
                  className="project-link"
                  title={projectSelected.nombre_proyecto}
                  target="_blank"
                >
                  {projectSelected.website_certificador} {/* Go to Project */}
                  {/** preguntar si este es el valor de aca*/}
                </Link>
              ) : null}
            </div>
            {/* <span
              className="project-modal_close"
              onClick={handleProjectModalClose}
            >
              <img src={CloseX} alt="Close modal" />
            </span> */}
            <IconButton
              className="close-modal"
              onClick={handleProjectModalClose}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <div className="project-info">
              <div className="project-info_location">
                <div className="project-info_location-pais">
                  <div>
                    <span className="project-info_keyTag project-info_keyTag-bottom">
                      {t('project.country').toUpperCase()}
                    </span>
                    <p>
                      <span
                        style={{
                          padding: 2,
                          backgroundColor: COUNTRY_COLORS[country],
                          color: country === 'peru' ? '#fff' : '#000',
                        }}
                      >
                        {projectSelected.id_pais.toUpperCase()}
                      </span>
                    </p>
                  </div>
                  <img
                    src={
                      country === 'brasil'
                        ? MBrasil
                        : country === 'colombia'
                          ? MColombia
                          : MPeru
                    }
                    alt="mapa"
                    className="project-info_location-map"
                  />
                </div>
                <div className="project-info_location-departamento">
                  <span className="project-info_keyTag project-info_keyTag-bottom">
                    {t('project.departament').toUpperCase()}
                  </span>{' '}
                  <p>
                    {projectSelected.departamento.length > 0
                      ? projectSelected.departamento.join(', ')
                      : t('no_disponible')}
                  </p>
                </div>
                <div className="project-info_location-municipio">
                  <span className="project-info_keyTag project-info_keyTag-bottom">
                    {t('project.municipality').toUpperCase()}
                  </span>{' '}
                  <p>{projectSelected.municipio}</p>
                </div>
                <div className="project-info_location-region">
                  <div>
                    <span className="project-info_keyTag project-info_keyTag-bottom">
                      {t('project.bioma_region').toUpperCase()}
                    </span>{' '}
                    <p>
                      {projectSelected.id_bioma
                        .map((id: string) => {
                          return t(`biomas.${id.trim()}`)
                        })
                        .join(', ')}
                    </p>
                  </div>
                  <div>
                    <span className="project-info_keyTag project-info_keyTag-bottom">
                      {t('project.amazonia').toUpperCase()}
                    </span>{' '}
                    <p>
                      {projectSelected.amazonia === '1' ? t('yes') : t('no')}
                    </p>
                  </div>
                </div>
                {/* <div>REGION | {projectSelected.pais}</div> */}
              </div>
              <div className="project-info_general">
                {/* left */}
                <div className="project-info_general-left">
                  <div>
                    <span className="project-info_keyTag project-info_keyTag-bottom">
                      {t('project.type_of_territory').toUpperCase()}
                    </span>{' '}
                    <p className="project-info_dataKey">
                      {projectSelected.id_tipo_territorio.map((id: string) => {
                        return t(`territory_type.${id.trim()}`)
                      }).join(', ')}
                    </p>
                  </div>
                  <div>
                    <span className="project-info_keyTag project-info_keyTag-bottom">
                      {t('project.territory').toUpperCase()}
                    </span>{' '}
                    <p className="project-info_dataKey">
                      {t(`territory.${projectSelected.id_territorio}`) ||
                        t('no_disponible')}
                    </p>
                  </div>
                  <div>
                    <span className="project-info_keyTag project-info_keyTag-bottom">
                      {t('project.etnic_groups').toUpperCase()}
                    </span>{' '}
                    <p className="project-info_dataKey">
                      {projectSelected.grupos_etnicos || t('no_disponible')}
                    </p>
                  </div>
                </div>
                {/* center */}
                <div className="project-info_general-center">
                  <div>
                    <span
                      className="project-info_keyTag project-info_keyTag-right"
                      style={{ width: '95px', textAlign: 'right' }}
                    >
                      {t('project.state').toUpperCase()}
                    </span>{' '}
                    <span className="project-info_dataKey">
                      {t(
                        `project.states.${projectSelected.id_estado}`
                      ).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <span
                      className="project-info_keyTag project-info_keyTag-right"
                      style={{ width: '95px', textAlign: 'right' }}
                    >
                      {t('project.duration_years').toUpperCase()}
                    </span>{' '}
                    <span className="project-info_dataKey">
                      {projectSelected.duracion || t('no_disponible')}
                    </span>
                  </div>
                  <div>
                    <span
                      className="project-info_keyTag project-info_keyTag-right"
                      style={{ width: '95px', textAlign: 'right' }}
                    >
                      {t('project.project_period').toUpperCase()}
                    </span>{' '}
                    <span className="project-info_dataKey">
                      {projectSelected.periodo || t('no_disponible')}
                    </span>
                  </div>
                  <div>
                    <span
                      className="project-info_keyTag project-info_keyTag-right"
                      style={{ width: '95px', textAlign: 'right' }}
                    >
                      {t('project.register_year').toUpperCase()}
                    </span>{' '}
                    <span className="project-info_dataKey">
                      {projectSelected.ano || t('no_disponible')}
                    </span>
                  </div>
                  <div>
                    <span
                      className="project-info_keyTag project-info_keyTag-right"
                      style={{ width: '95px', textAlign: 'right' }}
                    >
                      {t('project.project_area').toUpperCase()}
                    </span>{' '}
                    <span className="project-info_dataKey">
                      {projectSelected?.area_proyecto ? formatArea(projectSelected?.area_proyecto) : t('no_disponible')}
                    </span>
                  </div>
                </div>
                {/* right */}
                <div className="project-info_general-right">
                  <div>
                    <div style={{ overflowY: 'auto' }}>
                      <span className="project-info_keyTag project-info_keyTag-bottom">
                        {t('project.developer').toUpperCase()}
                      </span>{' '}
                      <p
                        className="project-info_dataKey"
                        style={{ fontSize: '18px' }}
                      >
                        {projectSelected.empresas_desarrolladoras.join(',\n') ||
                          t('no_disponible')}
                      </p>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          padding: 0,
                          // justifyContent: 'flex-end',
                          alignItems: 'flex-end',
                        }}
                      >
                        {projectSelected.url_pdd ? (
                          <a
                            href={projectSelected.url_pdd}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontSize: '14px',
                              color: '#95C11C',
                              // width: '100px',
                              // padding: '0 0 0 10px',
                              margin: 0,
                              fontWeight: 'bold',
                            }}
                          >
                            {t('project.downloadDocument')}
                          </a>
                        ) : null}

                        {/* <QRCodeSVG
                          style={{
                            border: '1px solid #000',
                            padding: 4,
                          }}
                          value={projectSelected.url_pdd}
                          size={120}
                          bgColor="#ffffff"
                          fgColor="#000000"
                        /> */}
                        {/* <p
                          style={{
                            fontSize: '12px',
                            color: '#95C11C',
                            width: '100px',
                            padding: '0 0 0 10px',
                            margin: 0,
                            fontWeight: 'bold',
                          }}
                        >
                          Este código QR lleva al documento de diseño del
                          proyecto (PDD)
                        </p> */}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div>
                      <span className="project-info_keyTag project-info_keyTag-bottom">
                        {t('project.certifier').toUpperCase()}
                      </span>
                      <p className="project-info_dataKey">
                        {projectSelected.estandar_certificacion ||
                          t('no_disponible')}
                      </p>
                      <Link
                        href={projectSelected.web_certificador}
                        color="#008e38"
                        sx={{
                          typography: 'body1',
                          fontSize: '10px',
                        }}
                      >
                        {projectSelected.web_certificador}
                      </Link>
                    </div>
                  </div>
                  <div>
                    <div>
                      <span className="project-info_keyTag project-info_keyTag-bottom">
                        {t('project.auditor').toUpperCase()}
                      </span>
                      <p className="project-info_dataKey">
                        {projectSelected.verificador || t('no_disponible')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {projectSelected[`perfil_${i18n.language.substring(0, 2)}`] ? (
              <div className="project-info_description">
                <div className="project-info_description-box">
                  <div className="project-info_description-text">
                    <p>
                      {
                        projectSelected[
                        `perfil_${i18n.language.substring(0, 2)}`
                        ]
                      }
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

            {projectLinks && projectLinks.length > 0 && (
              <div className="project-info_links">
                {/* <div className="project-info_button"> */}
                <ul className="project-info_links-list">
                  <li
                    style={
                      {
                        // width: (100 / projectLinks.length + 1) / 100 + '%',
                      }
                    }
                  >
                    <span style={{ fontWeight: 'bold', color: '#FFF' }}>
                      {t('project.knowTheStories')}:
                    </span>
                  </li>
                  {projectLinks?.map((link: any, index: number) => (
                    <li
                      key={index}
                      style={
                        {
                          // width: (100 / projectLinks.length + 1) / 100 + '%',
                        }
                      }
                    >
                      <Link
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="#008e38"
                      // sx={{
                      //   typography: 'body1',
                      //   fontSize: '12px',
                      // }}
                      >
                        {t('project.story')} {index + 1}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  )
}
